import { Close as CloseIcon, Menu as MenuIcon } from "@mui/icons-material";
import {
    AppBar,
    Box,
    Divider,
    Drawer,
    IconButton,
    Toolbar,
    useMediaQuery,
    useTheme
} from "@mui/material";
import { alpha } from '@mui/material/styles';
import React, { useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import { FadedPaper } from "src/theme/StyledFields";
import { useAppContext } from "../context/AppContext";
import { useNativeWebViewInsets } from "../context/NativeWebViewInsetsContext";
import Logo from "../theme/Logo";
import { ThemeContext } from "../theme/ThemeContext";
import ThemeSwitcher from "../theme/ThemeSwitcher";
import { nativeInsetSx } from "../utils/nativeWebViewInsets";
import RoleBasedMenu from "./RoleBasedMenu";

const BrandHeaders: React.FC = () => {
    const nativeInsets = useNativeWebViewInsets();
    const { organization, drawerOpen, toggleDrawer, closeDrawer } = useAppContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { brandingSettings, updateOrganizationName, updateOrganizationShortName, updateLogoUrl, updateLogoDarkUrl, updateLogoIconUrl, updateLogoIconDarkUrl } = useContext(ThemeContext);

    // Sync organization data with branding settings
    useEffect(() => {
        if (organization) {
            if (organization.name && !brandingSettings.organizationName) {
                updateOrganizationName(organization.name);
            }
            if (organization.short_name && !brandingSettings.organizationShortName) {
                updateOrganizationShortName(organization.short_name);
            }
            if (organization.logo && !brandingSettings.logoUrl) {
                updateLogoUrl(organization.logo);
            }
            if (organization.logo_dark && !brandingSettings.logoDarkUrl) {
                updateLogoDarkUrl(organization.logo_dark);
            }
            if (organization.logo_icon && !brandingSettings.logoIconUrl) {
                updateLogoIconUrl(organization.logo_icon);
            }
            if (organization.logo_icon_dark && !brandingSettings.logoIconDarkUrl) {
                updateLogoIconDarkUrl(organization.logo_icon_dark);
            }
        }
    }, [organization, brandingSettings, updateOrganizationName, updateOrganizationShortName, updateLogoUrl, updateLogoDarkUrl, updateLogoIconUrl, updateLogoIconDarkUrl]);


    const drawer = (
        <Box sx={{ zIndex: 250, minWidth: '150px' }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1,
                ...nativeInsetSx(nativeInsets, "top"),
                ...nativeInsetSx(nativeInsets, "horizontal"),
            }}>
                <Logo height={30} />
                <IconButton onClick={closeDrawer} data-attr="public-header_drawer-close">
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{
                p: 1, display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'space-between', alignItems: 'stretch'
            }}>
                <RoleBasedMenu layout="drawer" />
                <Divider sx={{ mb: 3, mt: 1 }} />
                <ThemeSwitcher />

            </Box>
        </Box>
    );

    return (
        <>
            <AppBar
                position="static"
                elevation={0}
                sx={(theme) => {
                    const isDark = theme.palette.mode === 'dark';
                    return {
                        backgroundColor: isDark ? alpha(theme.palette.secondary.dark, 0.8) : theme.palette.grey[900],
                        color: isDark ? theme.palette.grey[50] : theme.palette.grey[50],
                        borderBottom: 1,
                        borderColor: 'divider'
                    };
                }}
            >
                <Toolbar sx={{
                    justifyContent: 'space-between',
                    ...nativeInsetSx(nativeInsets, "top"),
                    ...nativeInsetSx(nativeInsets, "horizontal"),
                }}>
                    {/* Logo */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                            {/* AppBar background is always dark (grey[900] in light mode, paper in dark mode), so force the dark-background logo variant for contrast */}
                            <Logo height={40} variant="dark" layout={isMobile ? 'icon' : 'full'} />
                        </Link>
                    </Box>

                    <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end', gap: 2, mr: 3 }}>
                        <RoleBasedMenu layout="header" />
                    </Box>

                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={toggleDrawer}
                        data-attr="public-header_drawer-toggle"
                    >
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>


            {/* Mobile Drawer */}
            <Drawer
                variant="temporary"
                anchor="right"
                open={drawerOpen}
                onClose={closeDrawer}
                color="transparent"
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile.
                }}
            >
                {drawer}


                <FadedPaper style={{ position: 'absolute', top: 0, left: 0, minHeight: '100vh', maxHeight: '100vh', width: '100%', padding: 0, margin: 0, zIndex: 0 }} />
            </Drawer>


        </>
    );
};

export default BrandHeaders;
